import React from 'react';
import NextHead from 'next/head';
import PropTypes from 'prop-types';
import dynamic from 'next/dynamic';
import getConfig from 'next/config';
import { makeCanonicalURL } from './LinkingData/util';

// Only run GA, GTM, Segment, etc. when in production
const {
  publicRuntimeConfig: { APP_ENV, ACCOUNTS_SERVICE_HOST },
} = getConfig();
const loadTrackingScripts = APP_ENV === 'prod';

const DwellTime = dynamic(() => import('./tracking/DwellTime'), { ssr: false });

// TODO: don't hardcode CDN urls
const Head = function({
  children,
  description,
  path,
  index = false,
  image = null,
  title = null,
  follow = true,
  linkingData = null,
}) {
  // Figure out subdomain to use for canonical and structured data
  let subdomain = 'www';

  const canonicalUrl = makeCanonicalURL(path);

  return (
    <>
      <NextHead>
        {children}
        {description && <meta name="description" content={description} />}
        <meta
          name="robots"
          content={`${!index ? 'no' : ''}index,${!follow ? 'no' : ''}follow`}
        />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:url" content={canonicalUrl} />
        {title && <meta property="og:title" content={title} />}
        {description && (
          <meta property="og:description" content={description} />
        )}
        {image && description && (
          <meta property="og:image:alt" content={description} />
        )}
        {image && <meta property="og:image" content={image} />}
        <meta
          name="google-signin-client_id"
          content="283427801117-gcsiu9p714dc31684r15mdoks9vupn6a.apps.googleusercontent.com"
        />
        <meta name="apple-mobile-web-app-title" content="Education.com" />
        <meta name="application-name" content="Education.com" />
        <link rel="preconnect" href={ACCOUNTS_SERVICE_HOST} />
        {loadTrackingScripts && (
          <link rel="preconnect" href="https://storage.googleapis.com" />
        )}
        {loadTrackingScripts && (
          <link rel="preconnect" href="https://www.fswadmin.com" />
        )}
        <meta name="page_type" content="page" />
        <link
          rel="shortcut icon"
          href="https://cdn.education.com/i/logo/favicon.ico"
        />
        <link
          rel="apple-touch-icon"
          sizes="57x57"
          href="https://cdn.education.com/i/logo/apple-touch-icon-57x57.png"
        />
        <link
          rel="apple-touch-icon"
          sizes="60x60"
          href="https://cdn.education.com/i/logo/apple-touch-icon-60x60.png"
        />
        <link
          rel="apple-touch-icon"
          sizes="72x72"
          href="https://cdn.education.com/i/logo/apple-touch-icon-72x72.png"
        />
        <link
          rel="apple-touch-icon"
          sizes="76x76"
          href="https://cdn.education.com/i/logo/apple-touch-icon-76x76.png"
        />
        <link
          rel="apple-touch-icon"
          sizes="114x114"
          href="https://cdn.education.com/i/logo/apple-touch-icon-114x114.png"
        />
        <link
          rel="apple-touch-icon"
          sizes="120x120"
          href="https://cdn.education.com/i/logo/apple-touch-icon-120x120.png"
        />
        <link
          rel="apple-touch-icon"
          sizes="144x144"
          href="https://cdn.education.com/i/logo/apple-touch-icon-144x144.png"
        />
        <link
          rel="apple-touch-icon"
          sizes="152x152"
          href="https://cdn.education.com/i/logo/apple-touch-icon-152x152.png"
        />
        <link
          rel="apple-touch-icon"
          sizes="180x180"
          href="https://cdn.education.com/i/logo/apple-touch-icon-180x180.png"
        />
        <link
          rel="icon"
          type="image/png"
          sizes="192x192"
          href="https://cdn.education.com/i/logo/favicon-192x192.png"
        />
        <link
          rel="icon"
          type="image/png"
          sizes="160x160"
          href="https://cdn.education.com/i/logo/favicon-160x160.png"
        />
        <link
          rel="icon"
          type="image/png"
          sizes="96x96"
          href="https://cdn.education.com/i/logo/favicon-96x96.png"
        />
        <link
          rel="icon"
          type="image/png"
          sizes="32x32"
          href="https://cdn.education.com/i/logo/favicon-32x32.png"
        />
        <link
          rel="icon"
          type="image/png"
          sizes="16x16"
          href="https://cdn.education.com/i/logo/favicon-16x16.png"
        />
        <meta name="theme-color" content="#ffffff" />

        <link
          rel="preload"
          href="https://cdn.education.com/themes/sky/fonts/TTNorms/tt-norms-medium.woff2"
          as="font"
          type="font/woff2"
          crossOrigin=""
        />
        <link
          rel="preload"
          href="https://cdn.education.com/themes/sky/fonts/TTNorms/tt-norms-regular.woff2"
          as="font"
          type="font/woff2"
          crossOrigin=""
        />
        <link
          rel="preload"
          href="https://cdn.education.com/themes/sky/fonts/edicons/edicons.woff2"
          as="font"
          type="font/woff2"
          crossOrigin=""
        />
        <style
          dangerouslySetInnerHTML={{
            __html: `
@font-face {
  font-family: 'TTNorms-Medium';
  font-display: fallback;
  src: url('https://cdn.education.com/themes/sky/fonts/TTNorms/tt-norms-medium.woff2') format('woff2'),
       url('https://cdn.education.com/themes/sky/fonts/TTNorms/tt-norms-medium.woff') format('woff');
}
@font-face {
  font-family: 'TTNorms';
  font-weight: 400;
  font-display: fallback;
  src: url('https://cdn.education.com/themes/sky/fonts/TTNorms/tt-norms-regular.woff2') format('woff2'),
       url('https://cdn.education.com/themes/sky/fonts/TTNorms/tt-norms-regular.woff') format('woff');
}
@font-face {
  font-family: 'TTNorms';
  font-weight: 700;
  font-display: fallback;
  src: url('https://cdn.education.com/themes/sky/fonts/TTNorms/tt-norms-medium.woff2') format('woff2'),
       url('https://cdn.education.com/themes/sky/fonts/TTNorms/tt-norms-medium.woff') format('woff');
}
@font-face {
  font-family: 'edicons';
  font-display: block;
  src: url('https://cdn.education.com/themes/sky/fonts/edicons/edicons.woff2') format('woff2'),
       url('https://cdn.education.com/themes/sky/fonts/edicons/edicons.woff') format('woff');
}
          `,
          }}
        />
      </NextHead>

      {/* EDENG-2993: Until we upgrade to Next.js 11, include <script> tags outside of head. Then, use next/script */}
      <script
        key="ld-json-0"
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'http://schema.org',
            '@type': 'Organization',
            legalName: 'Education.com',
            url: `https://${subdomain}.education.com`,
            logo: '//www.fswadmin.com/i/logo/EDU-Logo-new-286x60.png',
            sameAs: [
              'https://www.facebook.com/Education.comFanPage',
              'https://twitter.com/education_com',
              'https://www.youtube.com/user/brainzyxeducationcom',
              'https://www.pinterest.com/education_com/',
            ],
          }),
        }}
      />
      <script
        key="ld-json-1"
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'http://schema.org',
            '@type': 'WebSite',
            name: 'Education.com',
            url: `https://${subdomain}.education.com`,
            potentialAction: {
              '@type': 'SearchAction',
              target: `https://${subdomain}.education.com/resources/?q={searchterm}`,
              'query-input': 'required name=searchterm',
            },
          }),
        }}
      />
      <script
        key="ld-json-2"
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(linkingData),
        }}
      />
      <DwellTime />
    </>
  );
};
Head.propTypes = {
  children: PropTypes.node,
  description: PropTypes.string,
  path: PropTypes.string,
  index: PropTypes.bool,
  image: PropTypes.string,
  title: PropTypes.string,
  follow: PropTypes.bool,
  linkingData: PropTypes.object,
};

export default Head;
